import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, Trash2, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  verified: { c: "chip-success", Icon: CheckCircle2, label: "verified" },
  pending: { c: "chip-warning", Icon: Clock, label: "pending" },
  failed: { c: "chip-danger", Icon: AlertCircle, label: "failed" },
};

export default function DomainsList() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const r = await api.get("/domains");
      setDomains(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDomains(); }, []);

  const removeDomain = async (id) => {
    try {
      await api.delete(`/domains/${id}`);
      toast.success("Domain removed");
      fetchDomains();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="domains-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold ink">Domains</h1>
          <p className="mt-1 ink-soft text-sm">All monitored domains across your organization.</p>
        </div>
        <Link to="/dashboard/domains/new" className="btn-accent" data-testid="add-domain-btn">
          <Plus className="h-4 w-4" /> Add domain
        </Link>
      </div>

      {loading ? (
        <div className="text-sm ink-soft">Loading…</div>
      ) : domains.length === 0 ? (
        <div className="panel p-16 text-center">
          <h2 className="font-display text-xl font-bold ink">No domains yet</h2>
          <p className="mt-2 ink-soft text-sm">Add your first domain to begin trust analysis.</p>
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider ink-soft">Domain</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider ink-soft">Status</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider ink-soft">Trust</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider ink-soft">Policy</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider ink-soft">Senders</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider ink-soft">Threats</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {domains.map((d) => {
                const s = STATUS[d.status] || STATUS.pending;
                return (
                  <tr key={d.domain_id} className="hover:bg-stone-50 transition-colors" data-testid={`domain-row-${d.domain}`}>
                    <td className="p-4">
                      <Link to={`/dashboard/domains/${d.domain_id}`} className="font-medium ink hover:text-orange-600">
                        {d.domain}
                      </Link>
                    </td>
                    <td className="p-4">
                      <span className={`chip ${s.c}`}>
                        <s.Icon className="h-3 w-3" />{s.label}
                      </span>
                    </td>
                    <td className="p-4 font-display font-bold ink">{d.status === "verified" ? d.trust_score : "—"}</td>
                    <td className="p-4 font-mono text-xs ink-soft">{d.dmarc_policy ? `p=${d.dmarc_policy}` : "—"}</td>
                    <td className="p-4 ink">{d.monitored_senders || "—"}</td>
                    <td className="p-4 ink">{d.status === "verified" ? d.active_threats : "—"}</td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => removeDomain(d.domain_id)}
                        className="text-stone-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50"
                        data-testid={`delete-${d.domain}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
